/**
 * DocumentViewer — Professional formatted document display
 *
 * Renders markdown as a polished, print-ready document with
 * paper styling, professional typography, and visual hierarchy.
 */

import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'

interface DocumentViewerProps {
  title: string
  text: string
  wordCount?: number
  versionLabel?: string
}

export function DocumentViewer({ title, text, wordCount, versionLabel }: DocumentViewerProps) {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-100 dark:bg-surface-950 py-8 px-4">
      {/* Paper container */}
      <div className="max-w-[816px] mx-auto bg-white dark:bg-surface-800 shadow-xl dark:shadow-2xl rounded-sm min-h-[1056px]">

        {/* Title block */}
        <div className="px-16 pt-16 pb-8 border-b-2 border-gray-900 dark:border-white/80">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight"
            style={{ fontFamily: "'Georgia', 'Times New Roman', 'Cambria', serif" }}
          >
            {title || 'Untitled Document'}
          </h1>
          <div className="flex items-center gap-3 mt-3 text-xs text-gray-400 dark:text-gray-500">
            {wordCount && (
              <span>{wordCount.toLocaleString()} words</span>
            )}
            {versionLabel && (
              <>
                <span className="text-gray-300 dark:text-gray-600">&middot;</span>
                <span>{versionLabel}</span>
              </>
            )}
            <span className="text-gray-300 dark:text-gray-600">&middot;</span>
            <span>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        {/* Document body */}
        <div className="px-16 py-10 editor-document">
          <ReactMarkdown
            rehypePlugins={[rehypeHighlight]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4 pb-2 border-b border-gray-200 dark:border-white/10 leading-tight"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-8 mb-3 leading-tight"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-2 leading-snug"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </h3>
              ),
              h4: ({ children }) => (
                <h4 className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2 uppercase tracking-wide text-sm">
                  {children}
                </h4>
              ),
              p: ({ children }) => (
                <p className="text-[15px] leading-[1.8] text-gray-700 dark:text-gray-300 mb-4"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[15px] text-gray-700 dark:text-gray-300"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[15px] text-gray-700 dark:text-gray-300"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="leading-[1.7] pl-1">{children}</li>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-5 py-1 my-5 italic text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/5 rounded-r-md"
                  style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {children}
                </blockquote>
              ),
              strong: ({ children }) => (
                <strong className="font-bold text-gray-900 dark:text-white">{children}</strong>
              ),
              em: ({ children }) => (
                <em className="italic text-gray-700 dark:text-gray-300">{children}</em>
              ),
              hr: () => (
                <hr className="my-8 border-t border-gray-200 dark:border-white/10" />
              ),
              table: ({ children }) => (
                <div className="my-6 overflow-x-auto rounded-md border border-gray-200 dark:border-white/15">
                  <table className="w-full text-sm">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                  {children}
                </thead>
              ),
              th: ({ children }) => (
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-white/5">
                  {children}
                </td>
              ),
              code: ({ className, children, ...props }) => {
                const isBlock = className?.includes('language-')
                if (isBlock) {
                  return (
                    <code className={`${className} block text-sm`} {...props}>
                      {children}
                    </code>
                  )
                }
                return (
                  <code className="px-1.5 py-0.5 text-[13px] bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200 rounded font-mono">
                    {children}
                  </code>
                )
              },
              pre: ({ children }) => (
                <pre className="my-5 p-4 bg-gray-900 dark:bg-black/50 text-gray-100 rounded-lg overflow-x-auto text-sm leading-relaxed">
                  {children}
                </pre>
              ),
              a: ({ href, children }) => (
                <a href={href} className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                  {children}
                </a>
              ),
            }}
          >
            {text}
          </ReactMarkdown>
        </div>

        {/* Footer */}
        <div className="px-16 py-4 border-t border-gray-100 dark:border-white/5">
          <div className="flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500">
            <span>REDTAPE Editor</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
